import classNames from "classnames";
import React from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { IPreScreenModalProps } from "tsi-common-react/src/apps/checkout/checkout-steps/BillingAddress";
import { FinancingModalTrigger } from "tsi-common-react/src/apps/financing/FinancingModalTrigger";
import { Link } from "tsi-common-react/src/common/Link";
import { Trans } from "tsi-common-react/src/common/Trans";
import { PreQualCustomerResponse } from "tsi-common-react/src/models/financing";
import { isoAPR, isoMonths } from "tsi-common-react/src/models/nominals";
import { listFinancingPlans } from "tsi-common-react/src/utils/financing";
import { focusElement } from "tsi-common-react/src/utils/keyboardFocus";
import { getPageSetting } from "tsi-common-react/src/utils/settings";
import { urls } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import tempurCreditCard from "../../img/checkout/tempur-credit-card.png";
import tempurPreApprovalCouple from "../../img/checkout/tempur-pre-approval-couple.jpg";
import iconInstantUse from "../../svg/instant-use.svg";
import iconNoAnnualFee from "../../svg/no-annual-fee.svg";
import iconXClose from "../../svg/x-close.svg";

interface IProps extends IPreScreenModalProps {}

interface IState {}

// TODO(ssr, LEVEL 6 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 6/10 due to complex modal UI with multiple event handlers, DOM queries for plan data, and extensive JSX structure with conditional rendering.
export class PreScreenModal extends React.Component<IProps, IState> {
    private readonly onClose = (
        e: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ) => {
        e.preventDefault();
        this.props.onRequestClose(PreQualCustomerResponse.CLOSE);
    };

    private readonly onAccept = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        this.props.onRequestClose(PreQualCustomerResponse.ACCEPT);
    };

    private readonly onReject = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        this.props.onRequestClose(PreQualCustomerResponse.REJECT);
    };

    render() {
        const modalStyle = {
            content: {
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                boxShadow: "none",
            },
        };
        const plans = listFinancingPlans();
        const bestPlan = plans.length > 0 ? plans[plans.length - 1] : null;
        let planAPR = 0;
        let planLength = 0;
        if (bestPlan) {
            planAPR = parseFloat(isoAPR.unwrap(bestPlan.apr));
            planLength = isoMonths.unwrap(bestPlan.length);
        }
        const bgImgStyle: React.CSSProperties = {
            backgroundImage: `url('${tempurPreApprovalCouple}')`,
        };
        const continueClass = classNames({
            "button": true,
            "checkout-step-pre-approval__button": true,
            "checkout-step-pre-approval__button--continue": true,
            "al-checkout-pre-approval__button--continue": true,
        });
        const rejectClass = classNames({
            "button": true,
            "button--inverse": true,
            "checkout-step-pre-approval__button": true,
            "checkout-step-pre-approval__button--cancel": true,
            "al-checkout-pre-approval__button--cancel": true,
        });
        return (
            <Modal
                aria={{ modal: true }}
                contentLabel={t`Pre-Approval`}
                style={modalStyle}
                className="checkout-step-pre-approval"
                isOpen={this.props.isOpen}
                onAfterOpen={() => {
                    focusElement(".checkout-step-pre-approval__close");
                }}
                onRequestClose={this.onClose}
                role="dialog"
            >
                <div className="checkout-step-pre-approval__header">
                    <button
                        aria-label={t`Close`}
                        className="checkout-step-pre-approval__close al-checkout-pre-approval__button--close"
                        onClick={this.onClose}
                    >
                        <SVG
                            aria-hidden="true"
                            src={iconXClose}
                            title={t`Close Icon`}
                        />
                    </button>
                    <h1 className="checkout-step-pre-approval__header-title">
                        <Trans
                            fmtString={t`The Tempur-Pedic<Superscript>®</Superscript> Credit Card`}
                            data={{
                                Superscript: (content) => (
                                    <span
                                        key="1"
                                        className="checkout-step-pre-approval__header-title--r"
                                    >
                                        <sup>{content}</sup>
                                    </span>
                                ),
                            }}
                        />
                    </h1>
                </div>
                <div className="checkout-step-pre-approval__body">
                    <div className="checkout-step-pre-approval__title-container">
                        <div className="checkout-step-pre-approval__title-image-container">
                            <img
                                src={tempurCreditCard}
                                className="checkout-step-pre-approval__title-image"
                            />
                        </div>
                        {/* TODO(i18n): Use jt to translate complete sentence with embedded components: jt`You're ${<span className="checkout-step-pre-approval__title--bold">pre-qualified<sup>*</sup></span>} ${<div className="checkout-step-pre-approval__pre-title">for a Tempur-Pedic<sup>®</sup> Credit Card account.</div>}` */}
                        <h2 className="checkout-step-pre-approval__title">
                            You&apos;re{" "}
                            <span className="checkout-step-pre-approval__title--bold">
                                pre-qualified<sup>*</sup>
                            </span>{" "}
                            <div className="checkout-step-pre-approval__pre-title">
                                for a Tempur-Pedic<sup>®</sup> Credit Card
                                account.
                            </div>
                        </h2>
                    </div>
                    <div className="checkout-step-pre-approval__content checkout-step-pre-approval__content--left">
                        <div className="checkout-step-pre-approval__data-box checkout-step-pre-approval__data-box--limit">
                            {/* TODO(i18n): Use jt to translate complete text with embedded <sup> tag: jt`pre-qualified${<sup>*</sup>} credit limit up to` */}
                            <div className="checkout-step-pre-approval__data-box-title">
                                pre-qualified<sup>*</sup> credit limit up to
                            </div>
                            {/* TODO(i18n): Use t tagged template with interpolation: {t`$${this.props.preApprovalLimit}`} */}
                            <div className="checkout-step-pre-approval__data-box-data">
                                ${this.props.preApprovalLimit}
                            </div>
                        </div>
                        <div className="checkout-step-pre-approval__data-box checkout-step-pre-approval__data-box--offer">
                            <div className="checkout-step-pre-approval__data-box-title">
                                {t`special financing offer`}
                            </div>
                            <div className="checkout-step-pre-approval__data-box-data">
                                {t`${planAPR}% APR for ${planLength} months`}
                                <sup>
                                    <Link href={urls.pageURL("finance_link")}>
                                        1
                                    </Link>
                                </sup>
                            </div>
                        </div>
                        <div className="checkout-step-pre-approval__value-props">
                            {/* TODO(i18n): Use jt to translate complete sentence with embedded <sup> tag: jt`No Annual Fee${<sup>1</sup>}` */}
                            <div className="checkout-step-pre-approval__value-prop">
                                <SVG
                                    className="checkout-step-pre-approval__value-icon checkout-step-pre-approval__value-icon--fee"
                                    title={t`Pre-Approved Icon`}
                                    src={iconNoAnnualFee}
                                />
                                {t`No Annual Fee`}
                                <sup>1</sup>
                            </div>
                            <div className="checkout-step-pre-approval__value-prop">
                                <SVG
                                    className="checkout-step-pre-approval__value-icon checkout-step-pre-approval__value-icon--instant"
                                    title={t`Use Now Icon`}
                                    src={iconInstantUse}
                                />
                                {t`Use instantly on Tempurpedic.com`}
                            </div>
                        </div>
                        {/* TODO(i18n): Use jt to translate complete sentence with embedded <span> tag: jt`You can choose to stop receiving "prescreened" offers of credit from this and other companies by calling toll-free 1-888-567-8688. See the ${<span>PRESCREEN & OPT-OUT NOTICE</span>} by selecting "Continue to Application" for more information about prescreened offers.` */}
                        <div className="checkout-step-pre-approval__opt-out">
                            {t`You can choose to stop receiving "prescreened" offers of credit from this and other companies by calling toll-free 1-888-567-8688. See the `}
                            <span>{t`PRESCREEN & OPT-OUT NOTICE`}</span>
                            {t` by selecting "Continue to Application" for more information about prescreened offers.`}
                        </div>
                    </div>
                    <div className="checkout-step-pre-approval__content checkout-step-pre-approval__content--right">
                        <div
                            className="checkout-step-pre-approval__image-container"
                            style={bgImgStyle}
                        ></div>
                        <div className="checkout-step-pre-approval__buttons">
                            <FinancingModalTrigger
                                modalType="full-app"
                                className={continueClass}
                                onClick={this.onAccept}
                            >
                                {t`Continue to Application`}
                            </FinancingModalTrigger>
                            <button
                                className={rejectClass}
                                onClick={this.onReject}
                            >
                                {t`Not now`}
                            </button>
                        </div>
                    </div>
                    <div
                        className="checkout-step-pre-approval__small-print"
                        dangerouslySetInnerHTML={{
                            __html:
                                getPageSetting(
                                    "pre_approval_modal_fine_print",
                                ) || "",
                        }}
                    ></div>
                    {/* TODO(i18n): Use jt to translate complete sentence with embedded <span> tag: jt`${<span>PRESCREEN & OPT-OUT NOTICE:</span>} This "prescreened" offer of credit is based on information in your credit report indicating that you meet certain criteria. This offer is not guaranteed if you do not meet our criteria. If you do not want to receive prescreened offers of credit from this and other companies, call the consumer reporting agencies toll-free at 1-888-567-8688, or write: Equifax, Inc., P.O. Box 740123, Atlanta, GA 30374-0123; TransUnion, P.O. Box 505, Woodlyn, PA 19094-0505; Experian, P.O. Box 919, Allen, TX 75013.` */}
                    <div className="checkout-step-pre-approval__opt-out-fine-print">
                        <span>{t`PRESCREEN & OPT-OUT NOTICE:`}</span>
                        {t` This "prescreened" offer of credit is based on information in your credit report indicating that you meet certain criteria. This offer is not guaranteed if you do not meet our criteria. If you do not want to receive prescreened offers of credit from this and other companies, call the consumer reporting agencies toll-free at 1-888-567-8688, or write: Equifax, Inc., P.O. Box 740123, Atlanta, GA 30374-0123; TransUnion, P.O. Box 505, Woodlyn, PA 19094-0505; Experian, P.O. Box 919, Allen, TX 75013.`}
                    </div>
                </div>
            </Modal>
        );
    }
}
